import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { PropsWithChildren, useState } from 'react';
import {
  type BookingRequestExtra,
  type BookingRequestRead,
  BookingRequestsExtraService,
  BookingRequestsService,
} from '../../../../api';
import { bookingRequestsQueryKeys } from '../../../query-keys-factories/booking-requests';
import { getFormattedTime } from '../BookingRequestDialog.utils';
import { ExtraIncome, ExtraIncomeContext } from './ExtraIncomeContext';

export type ExtraIncomeProviderProps = PropsWithChildren & {
  requestId: string;
};

export const ExtraIncomeProvider = ({ children, requestId }: ExtraIncomeProviderProps) => {
  const [extraIncome, setExtraIncome] = useState<ExtraIncome>({});
  const queryClient = useQueryClient();

  const updateExtra = (data: BookingRequestRead) => {
    const {
      extra_hours_time_spent_minutes,
      extra_hours_total_amount,
      additional_charges_travel_expenses,
      additional_charges_bonus,
    } = data.booking_request_extra;
    setExtraIncome({
      spentMinutes: getFormattedTime(extra_hours_time_spent_minutes),
      totalAmount: parseInt(extra_hours_total_amount),
      additionalBonus: parseFloat(additional_charges_bonus),
      travelExpenses: parseFloat(additional_charges_travel_expenses),
    });
  };

  const { isLoading: isExtraDataLoading, refetch: refetchExtraData } = useQuery({
    queryKey: bookingRequestsQueryKeys.extra(requestId),
    queryFn: async () => await BookingRequestsService.bookingRequestsRetrieve({ requestId }),
    enabled: !!requestId,
    onSuccess: (data: BookingRequestRead) => {
      if (data.booking_request_extra) {
        return updateExtra(data);
      }
    },
  });

  const { mutateAsync: saveBookingRequestExtra, isLoading: isExtraDataSaveLoading } = useMutation({
    mutationFn: async ({
      requestId,
      requestBody,
    }: {
      requestId: string;
      requestBody?: BookingRequestExtra;
    }) => {
      return BookingRequestsExtraService.bookingRequestsExtraCreate({
        requestId,
        requestBody,
      });
    },
    onSuccess: () => queryClient.invalidateQueries(bookingRequestsQueryKeys.extra(requestId)),
  });

  return (
    <ExtraIncomeContext.Provider
      value={{
        requestId,
        extraIncome,
        isLoading: isExtraDataLoading || isExtraDataSaveLoading,
        refetchExtraData,
        saveBookingRequestExtra,
      }}
    >
      {children}
    </ExtraIncomeContext.Provider>
  );
};
